import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { guardarClientesAsignados, obtenerClientesAsignados } from '../db/indexedDB';
import { API_BASE, formatApiError, getToken, isAuthError, notifySessionExpired } from '../services/api';

const ordenarPorMonto = (items) => (
  [...items].sort((a, b) => (
    Number(b.prioridad_admin || 0) - Number(a.prioridad_admin || 0) ||
    Number(b.deuda_vencida || 0) - Number(a.deuda_vencida || 0)
  ))
);

const clienteVisibleParaTecnico = (cliente) => !['finalizado', 'revision_admin', 'reprogramado'].includes(cliente.estado);
const tieneCoordenadas = (cliente) => (
  cliente.latitud !== null &&
  cliente.longitud !== null &&
  cliente.latitud !== undefined &&
  cliente.longitud !== undefined
);

const visitMarkerIcon = L.divIcon({
  className: 'gv-map-marker',
  html: '<span></span>',
  iconSize: [30, 38],
  iconAnchor: [15, 36],
  popupAnchor: [0, -34],
});

const MapaTecnico = ({ onSelectCliente }) => {
  const [posiciones, setPosiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const defaultCenter = [-33.0456, -71.6157];
  const posicionesConMapa = posiciones.filter(tieneCoordenadas);
  const posicionesSinMapa = posiciones.filter((pos) => !tieneCoordenadas(pos));
  const deudaVisible = posiciones.reduce((total, pos) => total + Number(pos.deuda_vencida || 0), 0);
  const mapCenter = useMemo(() => {
    if (!posicionesConMapa.length) return defaultCenter;
    return [posicionesConMapa[0].latitud, posicionesConMapa[0].longitud];
  }, [posicionesConMapa]);

  useEffect(() => {
    fetch(`${API_BASE}/tecnicos/mis-clientes`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          if (isAuthError(res, data)) notifySessionExpired();
          throw new Error(formatApiError(res, data, 'No se pudieron cargar los clientes asignados.'));
        }
        const clientesAsignados = ordenarPorMonto(Array.isArray(data) ? data : []);
        setPosiciones(clientesAsignados);
        await guardarClientesAsignados(clientesAsignados);
      })
      .catch(async (err) => {
        const cachedClientes = await obtenerClientesAsignados().catch(() => []);
        const visibles = cachedClientes.filter(clienteVisibleParaTecnico);
        if (visibles.length) {
          setPosiciones(ordenarPorMonto(visibles));
          setError('Sin señal: usando clientes guardados en este dispositivo.');
          return;
        }
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="map-view">
      <div className="map-view__header">
        <div>
          <span className="map-view__eyebrow">Ruta diaria</span>
          <h2>Mapa de visitas</h2>
          <p>Ubica los casos con coordenadas y detecta rápidamente cuáles requieren confirmar dirección.</p>
        </div>
      </div>

      {!loading && posiciones.length > 0 && (
        <section className="route-summary route-summary--map" aria-label="Resumen del mapa">
          <div><span>Casos asignados</span><strong>{posiciones.length}</strong><small>Carga visible</small></div>
          <div><span>En el mapa</span><strong>{posicionesConMapa.length}</strong><small>Con coordenadas</small></div>
          <div className={posicionesSinMapa.length ? 'route-summary__attention' : ''}><span>Sin coordenadas</span><strong>{posicionesSinMapa.length}</strong><small>{posicionesSinMapa.length ? 'Requieren revisión' : 'Cobertura completa'}</small></div>
          <div className="route-summary__debt"><span>Deuda visible</span><strong>${deudaVisible.toLocaleString('es-CL')}</strong><small>Total asignado</small></div>
        </section>
      )}

      <div className="map-shell">
        <MapContainer key={mapCenter.join(',')} center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {posicionesConMapa.map((pos) => (
              <Marker key={pos.id} position={[pos.latitud, pos.longitud]} icon={visitMarkerIcon}>
                <Popup>
                  <strong>{pos.cod_cliente} | Gestión {pos.num_gestion}</strong>
                  <br />
                  {pos.r_social}
                  <br />
                  {pos.direccion}
                  {pos.coordenada_aproximada && (
                    <>
                      <br />
                      Coordenada aproximada por comuna.
                    </>
                  )}
                  <br />
                  <button type="button" className="gv-btn gv-btn--primary gv-btn--sm" onClick={() => onSelectCliente?.(pos)}>
                    Ver ficha
                  </button>
                </Popup>
              </Marker>
          ))}
        </MapContainer>
        {loading && <div className="map-empty-state">Cargando clientes asignados...</div>}
        {error && <div className="map-empty-state" role="status">{error}</div>}
        {!loading && posicionesConMapa.length === 0 && (
          <div className="map-empty-state">
            No hay clientes con coordenadas para mostrar en el mapa.
          </div>
        )}
      </div>

      {!loading && posiciones.length > 0 && (
        <section className="map-list-section">
          <div className="map-list-section__heading">
            <div>
              <span className="map-view__eyebrow">Detalle operativo</span>
              <h3>Casos asignados</h3>
            </div>
            <p>Los casos sin coordenadas siguen disponibles para abrir su ficha.</p>
          </div>
          <div className="map-assigned-list">
          {posiciones.map((pos, index) => (
            <button key={pos.id} type="button" onClick={() => onSelectCliente?.(pos)}>
              <span className="map-assigned-list__number">{index + 1}</span>
              <span className="map-assigned-list__copy">
                <strong>{pos.r_social || `Cliente ${pos.cod_cliente}`}</strong>
                <small>Gestión {pos.num_gestion} · {pos.comuna || 'Sin comuna'} · ${Number(pos.deuda_vencida || 0).toLocaleString('es-CL')}</small>
              </span>
              <span className={`map-assigned-list__state ${tieneCoordenadas(pos) ? 'ok' : 'warning'}`}>
                {tieneCoordenadas(pos) ? 'En mapa' : 'Sin coordenadas'}
              </span>
            </button>
          ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default MapaTecnico;
